import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { UIPaginationBar } from "../../../ui";
import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { EVALUATION_ROUND_STATUSES } from "./constants";
import ApplicantEvaluationsFilters from "./ApplicantEvaluationsFilters";
import ApplicantEvaluationsTable from "./ApplicantEvaluationsTable";
import useApplicantEvaluations from "./useApplicantEvaluations";

const EvaluationRoundApplicants = ({
  round,
  event,
  readOnly,
  canSubmit,
  canSubmitAll,
  portalVariant,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const t = messages[lang].events.viewEvents.evaluationsTab.applicantsView;
  const history = useHistory();

  const {
    search,
    filters,
    sortModel,
    page,
    pageSize,
    rows,
    total,
    pages,
    rangeFrom,
    rangeTo,
    zoneOptions,
    roleOptions,
    statusOptions,
    isFetching,
    handleSearchChange,
    handleFilterChange,
    handleSortModelChange,
    handlePageChange,
    handlePageSizeChange,
  } = useApplicantEvaluations(round.id, portalVariant);

  const isAdminPortal = portalVariant === "admin-portal";
  const isFiltered = Boolean(
    search.trim() || filters.zone || filters.role || filters.status,
  );

  const handleViewResult = (applicant) => {
    const resultPath = isAdminPortal
      ? ROUTE_PATHS.adminApplicantEvaluationResult
      : ROUTE_PATHS.ApplicantEvaluationResult;
    history.push({
      pathname: resultPath
        .replace(":eventId", event?.id || "0")
        .replace(":roundId", round.id)
        .replace(":applicantId", applicant.id),
      state: {
        applicant,
        round,
        event,
        portalVariant,
      },
    });
  };

  const handleEvaluate = (applicant) => {
    const evaluatePath = isAdminPortal
      ? ROUTE_PATHS.adminEvaluateApplicant
      : ROUTE_PATHS.companyEvaluateApplicant;
    history.push({
      pathname: evaluatePath
        .replace(":eventId", event?.id || "0")
        .replace(":roundId", round.id)
        .replace(":applicantId", applicant.id),
      state: { applicant, round, event },
    });
  };

  const renderRunningIndicator = () => {
    if (round.status !== EVALUATION_ROUND_STATUSES.running) return null;
    return (
      <Box className="eval-rounds__running-badge">
        <span className="eval-rounds__running-dot" />
        <Typography variant="body2" className="eval-rounds__running-label">
          {t.runningIndicator.replace("{name}", round.name)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box className="eval-rounds">
      <ApplicantEvaluationsFilters
        copy={t}
        search={search}
        filters={filters}
        zoneOptions={zoneOptions}
        roleOptions={roleOptions}
        statusOptions={statusOptions}
        renderActionBeforeFilterToggle={renderRunningIndicator()}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
      />

      <ApplicantEvaluationsTable
        rows={rows}
        labels={t.table}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onViewResult={handleViewResult}
        onEvaluate={handleEvaluate}
        readOnly={readOnly}
        canSubmit={canSubmit}
        canSubmitAll={canSubmitAll}
        isLoading={isFetching}
        isFiltered={isFiltered}
      />

      <UIPaginationBar
        currentPage={page}
        pages={pages}
        pageSize={pageSize}
        rangeTemplate={t.rangeTemplate}
        rangeFrom={rangeFrom}
        rangeTo={rangeTo}
        rangeTotal={total}
        rowsPerPageLabel={t.rowsPerPageLabel}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </Box>
  );
};

EvaluationRoundApplicants.propTypes = {
  round: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
  }).isRequired,
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    status: PropTypes.string,
  }),
  readOnly: PropTypes.bool,
  canSubmit: PropTypes.bool,
  canSubmitAll: PropTypes.bool,
  portalVariant: PropTypes.oneOf(["company-portal", "admin-portal"]),
};

EvaluationRoundApplicants.defaultProps = {
  event: null,
  readOnly: false,
  canSubmit: false,
  canSubmitAll: false,
  portalVariant: "company-portal",
};

export default EvaluationRoundApplicants;
